import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationControls from '../../components/PaginationControls';
import { getAllProducts, deleteProduct, updateProduct, createProduct } from '../../API/Crud/Product';
import ConfirmationModal from '../../components/ConfirmationModal';
import DashboardTemplate from '../template/DashboardTemplate';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { get, post, upload } from '../../services/ApiRouting';
import { useDropzone } from "react-dropzone";
import { BiEdit, BiTrash } from 'react-icons/bi';
import { getAllCategories } from '../../API/Crud/Category';

function Products() {
  const [Products, setProducts] = useState([]);
  const [Categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ useForm with setValue
  const { register, handleSubmit, control, reset, setValue, formState: { errors }, clearErrors } = useForm({
    defaultValues: { name: "", category: "", description: "", file: [] }
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // ✅ formData declared BEFORE any useEffect that uses it
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    category: '',
    description: '',
    file: [],
  });

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // ✅ Single useEffect for selectedProduct — no duplicate
  useEffect(() => {
    if (selectedProduct) {
      const images = selectedProduct.images || [];
      reset({
        name: selectedProduct.name || "",
        category: selectedProduct.category_id || "",
        description: selectedProduct.description || "",
        file: images,
      });
      // ✅ Sync local state
      setUploadedFiles(images);
      // ✅ Sync RHF so validation passes
      setValue('file', images.length > 0 ? images : []);
    }
  }, [selectedProduct, reset]);

  // Token effect
  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      console.error('No token found');
      setError('No token found. Please log in.');
    }
  }, []);

  // Fetch products & categories
  useEffect(() => {
    if (!token) return;

    async function fetchProducts() {
      setLoading(true);
      try {
        const data = await getAllProducts();
        const cats = await getAllCategories();
        setCategories(cats.data);
        setProducts(data);
        setError(null);
      } catch (error) {
        setError(error.message || 'An unknown error occurred');
        toast.error('Failed to load Products');
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [token]);

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteProduct(productId);
      setProducts((prevProducts) => prevProducts.filter(product => product.id !== productId));
      toast.success('Product deleted successfully');
    } catch (error) {
      setError('Error deleting product');
      toast.error('Failed to delete product');
    }
  };

  // ✅ handleEditProduct — setUploadedFiles is active (not commented out)
  const handleEditProduct = (product) => {
    setSelectedProduct(product); // triggers the useEffect above
    setFormData({
      id: product.id,
      name: product.name,
      category: product.category_id,
      description: product.description,
      file: product.images,
    });
    setUploadedFiles(product.images || []); // ✅ sync local state
    setShowEditModal(true);
  };

  // Upload function
  const uploadFiles = async (files, field) => {
    setIsUploading(true);
    const uploadPromises = files.map(async (file) => {
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file, file.name);

        const response = await upload(`${API_URL}/crud/products/fileUpload`, uploadFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const serverFilePath = response.fileId || response.data.file_path;
        return serverFilePath;
      } catch (err) {
        console.error("Upload error:", err);
        return null;
      }
    });

    const serverIds = await Promise.all(uploadPromises);
    const validServerIds = serverIds.filter(Boolean);

    setUploadedFiles(prev => {
      const updated = [...prev, ...validServerIds];
      field.onChange(updated); // ✅ sync RHF with latest value
      return updated;
    });

    setIsUploading(false);
  };

  // Remove file function
  const removeFile = (index, field) => {
    setUploadedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      field.onChange(newFiles.length > 0 ? newFiles : []); // ✅ sync RHF
      return newFiles;
    });
  };

  const onSubmit = async (data) => {
    const productData = {
      ...data,
      file: uploadedFiles,
    };

    try {
      const response = await createProduct(productData);
      if (response.ok) {
        setShowModal(false);
        toast.success('Product added successfully');
        setProducts((prevProducts) => [...prevProducts, response.data]);
        reset({ name: "", category: "", description: "", file: [] });
        setUploadedFiles([]);
        await refreshProducts();
      }
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const refreshProducts = async () => {
    const data = await getAllProducts();
    setProducts(data);
  };

  const onUpdateSubmit = async (data) => {
    const productData = {
      ...data,
      file: uploadedFiles.map(f => f.image_url ?? f),
    };

    try {
      const response = await updateProduct(selectedProduct.id, productData);
      if (response.ok) {
        setShowEditModal(false);
        toast.success('Product updated successfully');
        await refreshProducts();
      }
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const baseUrl = `${API_PUBLIC_URL}`;

  const handleConfirmDelete = () => {
    if (productToDelete) {
      handleDeleteProduct(productToDelete.id);
      setShowConfirmModal(false);
      setProductToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setProductToDelete(null);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Products.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Shared dropzone renderer for both Create and Edit
  const renderDropzone = (field) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/jpeg': ['.jpeg', '.jpg'],
        'image/png': ['.png'],
      },
      multiple: true,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          uploadFiles(acceptedFiles, field);
        }
      },
    });

    return (
      <>
        <div
          {...getRootProps()}
          className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors"
        >
          <input {...getInputProps()} />
          <div className="text-center">
            {isDragActive ? (
              <p className="text-blue-600">Drop the files here ...</p>
            ) : (
              <div>
                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
              </div>
            )}
          </div>
        </div>

        {isUploading && (
          <div className="mb-4">
            <p className="text-blue-600">Uploading files...</p>
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Uploaded Files:</h4>
            <ul className="space-y-2">
              {uploadedFiles.map((fileId, i) => (
                <li key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <div className="flex items-center gap-2">
                    <img
                      width={100}
                      src={baseUrl + (fileId.image_url || fileId)}
                      alt=""
                      className="rounded-full"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i, field)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  return (
    <DashboardTemplate>
      <div className="flex flex-col">
        <ToastContainer />
        <div className="transition-all duration-500 flex-1">
          <div className="content p-2 md:p-5">

            {loading ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 animate-spin fill-orange-600 block mx-auto" viewBox="0 0 24 24">
                <path d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z" />
              </svg>
            ) : error ? (
              <p>Error: {error}</p>
            ) : (
              <div className="font-[sans-serif]">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">Products</h1>
                  <button
                    className="bg-[#F2A549] font-normal cursor-pointer text-black px-4 py-2 rounded hover:bg-[#efb46f]"
                    onClick={() => {
                      reset({ name: "", category: "", description: "", file: [] });
                      setUploadedFiles([]);
                      setShowModal(true);
                    }}
                  >
                    Add New Product
                  </button>
                </div>

                <div className="w-auto max-w-full overflow-x-auto">
                  <table className="border-collapse min-w-full border border-gray-300 rounded-lg">
                    <thead className="bg-[#F2A549] text-white">
                      <tr className="text-center">
                        <th className="p-4 font-normal text-black">Images</th>
                        <th className="p-4 font-normal text-black">Title</th>
                        <th className="p-4 font-normal text-black">Description</th>
                        <th className="p-4 font-normal text-black">Date</th>
                        <th className="p-4 font-normal text-black">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(Products) && [...currentItems].reverse().map((product) => (
                        <tr className="border border-gray-300" key={product.id}>
                          <td className="border border-gray-300 py-2">
                            <div className="flex -space-x-2 overflow-hidden justify-center">
                              {Array.isArray(product.images) && product.images.map((img, index) => (
                                <img
                                  alt=""
                                  key={index}
                                  src={baseUrl + img.image_url}
                                  className="inline-block w-15 h-15 mx-auto size-8 rounded-full ring-2 ring-white"
                                />
                              ))}
                            </div>
                          </td>
                          <td className="text-center text-sm border border-gray-300">{product.name}</td>
                          <td className="relative w-[20%] border border-gray-300 text-sm text-center">
                            <div className="w-[90px] truncate inline-block align-middle">
                              {product.description.replace(/<\/?[^>]+(>|$)/g, "")}
                            </div>
                          </td>
                          <td className="text-sm border border-gray-300 text-center">{formatDate(product.created_at)}</td>
                          <td className="border border-gray-300 text-center">
                            <div className="flex justify-center gap-4">
                              <button className="cursor-pointer" title="Edit" onClick={() => handleEditProduct(product)}>
                                <BiEdit size={25} />
                              </button>
                              <button className="cursor-pointer" title="Delete" onClick={() => handleDeleteProduct(product.id)}>
                                <BiTrash size={25} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {Products.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-gray-500 border border-gray-300">
                            No Products available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ===== CREATE MODAL ===== */}
                <Dialog open={showModal} onClose={setShowModal} className="relative z-[9999]">
                  <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                  />
                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95">
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">

                            {/* Title */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Title</label>
                              <div className="mt-2">
                                <input
                                  id="name"
                                  name="name"
                                  type="text"
                                  autoComplete="name"
                                  onChange={handleChange}
                                  {...register('name', { required: 'Title is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.name && <span className="error-message">{errors.name.message}</span>}
                              </div>
                            </div>

                            {/* Category */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Product category</label>
                              <div className="mt-2">
                                <select
                                  id="category"
                                  name="category"
                                  {...register('category', { required: 'Category is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                >
                                  {Array.isArray(Categories) && Categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                  ))}
                                  {Array.isArray(Categories) && Categories.length === 0 && (
                                    <option value="">No categories available</option>
                                  )}
                                </select>
                                {errors.category && <span className="error-message">{errors.category.message}</span>}
                              </div>
                            </div>

                            {/* Description */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Description</label>
                              <div className="mt-2">
                                <Controller
                                  name="description"
                                  control={control}
                                  rules={{ required: "Description is required" }}
                                  render={({ field }) => (
                                    <CKEditor
                                      editor={ClassicEditor}
                                      data={field.value}
                                      onChange={(event, editor) => field.onChange(editor.getData())}
                                    />
                                  )}
                                />
                                {errors.description && <span className="error-message">{errors.description.message}</span>}
                              </div>
                            </div>

                            {/* Images */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Images</label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  defaultValue={[]}
                                  rules={{
                                    required: 'Images are required',
                                    validate: (value) => (value && value.length > 0) || 'Images are required',
                                  }}
                                  render={({ field }) => renderDropzone(field)}
                                />
                                {errors.file && <span className="error-message">{errors.file.message}</span>}
                              </div>
                            </div>

                          </div>
                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="submit" className="inline-flex w-full cursor-pointer justify-center rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto">
                              Create
                            </button>
                            <button type="button" onClick={() => setShowModal(false)} className="mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>

                {/* ===== EDIT MODAL ===== */}
                <Dialog open={showEditModal} onClose={setShowEditModal} className="relative z-[9999]">
                  <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                  />
                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95">
                        <form onSubmit={handleSubmit(onUpdateSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">

                            {/* Title */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Title</label>
                              <div className="mt-2">
                                <input
                                  id="name"
                                  name="name"
                                  type="text"
                                  autoComplete="name"
                                  {...register('name', { required: 'Title is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.name && <span className="error-message">{errors.name.message}</span>}
                              </div>
                            </div>

                            {/* Category */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Product category</label>
                              <div className="mt-2">
                                <select
                                  id="category"
                                  name="category"
                                  {...register('category', { required: 'Category is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                >
                                  {Array.isArray(Categories) && Categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                  ))}
                                  {Array.isArray(Categories) && Categories.length === 0 && (
                                    <option value="">No categories available</option>
                                  )}
                                </select>
                                {errors.category && <span className="error-message">{errors.category.message}</span>}
                              </div>
                            </div>

                            {/* Description */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Description</label>
                              <div className="mt-2">
                                <Controller
                                  name="description"
                                  control={control}
                                  rules={{ required: "Description is required" }}
                                  render={({ field }) => (
                                    <CKEditor
                                      editor={ClassicEditor}
                                      data={formData.description}
                                      onChange={(event, editor) => field.onChange(editor.getData())}
                                    />
                                  )}
                                />
                                {errors.description && <span className="error-message">{errors.description.message}</span>}
                              </div>
                            </div>

                            {/* Images */}
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Images</label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  defaultValue={[]}
                                  rules={{
                                    validate: (value) => (value && value.length > 0) || 'Images are required',
                                  }}
                                  render={({ field }) => renderDropzone(field)}
                                />
                                {errors.file && <span className="error-message">{errors.file.message}</span>}
                              </div>
                            </div>

                          </div>
                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="submit" className="inline-flex w-full justify-center cursor-pointer rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto">
                              Update
                            </button>
                            <button type="button" onClick={() => setShowEditModal(false)} className="mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>

                <PaginationControls
                  itemsPerPage={itemsPerPage}
                  totalItems={Products.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>
        </div>

        <ConfirmationModal
          show={showConfirmModal}
          title={productToDelete ? productToDelete.title : ''}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>
    </DashboardTemplate>
  );
}

export default Products;